import { Table, Tag, Tooltip, message } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  ModalItem,
  InputNumberItem,
  NotificacionMovimientosInventario,
  round10Decimals,
} from "../../../../Components/Items";

// Modal de Apartar / Desapartar de una Orden de Productos al Costo.
//
// Es el mismo componente para los dos sentidos (prop `modo`): la unica
// diferencia es de donde sale el maximo de cada renglon y a que endpoint se
// manda. El apartado es PARCIAL: se puede bajar la cantidad propuesta y volver
// a abrir el modal las veces que haga falta conforme entre inventario.
//
// Los renglones con lotes, marbetes o agrupaciones no se pueden apartar: el
// apartado recrea las EntradaSalida con _id nuevos y los entradaId elegidos al
// capturar quedarian obsoletos. Salen deshabilitados con la razon a la vista.
class ModalApartarProductosAlCosto extends Component {
  _isMounted = false;

  state = {
    loading: false,
    guardando: false,
    renglones: [],
  };

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.consultarOrden();
    }
    if (!this.props.visible && prevProps.visible) {
      this.setState({ renglones: [] });
    }
  }

  get esApartar() {
    return this.props.modo !== "desapartar";
  }

  // El catalogo trae los insumos reales; un renglon puede apuntar a una
  // equivalencia, asi que se busca tambien dentro de `equivalencias`.
  buscarInsumoCatalogo(insumosCatalogo, insumoId) {
    if (!insumoId) return undefined;
    const id = insumoId.toString();
    return (insumosCatalogo || []).find(
      (valueInsumo) =>
        valueInsumo._id.toString() === id ||
        (valueInsumo.equivalencias || []).some((valueEq) => valueEq._id.toString() === id)
    );
  }

  async consultarOrden() {
    this.setState({ loading: true, renglones: [] });
    try {
      const res = await axios.post(
        "productosAlCosto/datosOrdenProductosAlCosto",
        { empresaId: this.props.empresaId, ordenId: this.props.ordenId },
        { headers: this.props.headersToken }
      );

      if (!this._isMounted) return;

      if (res.data.status === 404) {
        message.error("No se encontró la orden");
        this.setState({ loading: false });
        return;
      }

      const insumosOrden = res.data.insumos || [];
      const insumosCatalogo = res.data.insumosCatalogo || [];

      // Disponible en el proyecto por renglon (solo se necesita para apartar).
      let disponiblePorIndice = new Map();
      if (this.esApartar && insumosOrden.length > 0) {
        try {
          const resInventarios = await axios.post(
            "inventarios/inventarioInsumoProyectoBulk",
            {
              empresaId: this.props.empresaId,
              proyectoId: res.data.proyectoId,
              insumos: insumosOrden.map((valueInsumo, index) => ({
                index,
                insumoId: valueInsumo.insumoId,
                proyectoId: res.data.proyectoId,
                inventariable: true,
              })),
            },
            { headers: this.props.headersToken }
          );
          (resInventarios.data?.results || []).forEach((resultado) => {
            disponiblePorIndice.set(resultado.index, Number(resultado.cantidad) || 0);
          });
        } catch (error) {
          console.error("Error al consultar inventario", error);
        }
      }

      if (!this._isMounted) return;

      const renglones = insumosOrden.map((valueInsumo, index) => {
        const insumo = this.buscarInsumoCatalogo(insumosCatalogo, valueInsumo.insumoId);
        const apartable =
          !!insumo &&
          !!insumo.inventariable &&
          !insumo.marbetes &&
          !insumo.lotesCaducidades &&
          !insumo.agrupaciones;

        const cantidad = Number(valueInsumo.cantidad) || 0;
        const apartado = Number(valueInsumo.apartado) || 0;
        const disponible = disponiblePorIndice.get(index) ?? 0;

        const maximo = this.esApartar
          ? round10Decimals(Math.max(Math.min(cantidad - apartado, disponible), 0))
          : apartado;

        return {
          key: index,
          index,
          descripcion: insumo
            ? `${insumo.clave ? insumo.clave + " - " : ""}${insumo.descripcion || ""}`
            : "Insumo no encontrado",
          unidad: insumo?.unidad || "",
          cantidad,
          apartado,
          disponible,
          apartable,
          maximo: this.esApartar && !apartable ? 0 : maximo,
          valor: this.esApartar && !apartable ? 0 : maximo,
        };
      });

      this.setState({ loading: false, renglones });
    } catch (error) {
      console.error(error);
      if (this._isMounted) {
        message.error("Hubo un error al consultar la orden");
        this.setState({ loading: false });
      }
    }
  }

  onChangeCantidad(index, valor) {
    this.setState((prev) => ({
      renglones: prev.renglones.map((renglon) => {
        if (renglon.index !== index) return renglon;
        const numero = Number(valor);
        const limpio = isNaN(numero) || numero < 0 ? 0 : numero;
        return { ...renglon, valor: round10Decimals(Math.min(limpio, renglon.maximo)) };
      }),
    }));
  }

  aceptar() {
    const insumos = this.state.renglones
      .filter((renglon) => Number(renglon.valor) > 0)
      .map((renglon) => ({ index: renglon.index, cantidad: Number(renglon.valor) }));

    if (insumos.length === 0) {
      return message.warning(
        this.esApartar ? "Indique la cantidad a apartar" : "Indique la cantidad a desapartar"
      );
    }

    this.setState({ guardando: true });
    axios
      .post(
        this.esApartar
          ? "productosAlCosto/apartarOrdenProductosAlCosto"
          : "productosAlCosto/desapartarOrdenProductosAlCosto",
        { ordenId: this.props.ordenId, empresaId: this.props.empresaId, insumos },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (!this._isMounted) return;
        this.setState({ guardando: false });
        if (res.data.success === 2) {
          message.success(
            this.esApartar ? "Se apartó el inventario" : "Se liberó el inventario apartado"
          );
          NotificacionMovimientosInventario(res.data.movimientosIds);
          this.props.cerrar(true);
        } else if (res.data.success === 1) {
          message.error(
            res.data.message ||
            (this.esApartar ? "No se pudo apartar el inventario" : "No se pudo liberar el apartado")
          );
        } else {
          message.error("Hubo un error y el inventario no se movió");
        }
      })
      .catch(() => {
        if (this._isMounted) {
          this.setState({ guardando: false });
          message.error("Hubo un error y el inventario no se movió");
        }
      });
  }

  render() {
    const esApartar = this.esApartar;

    const columns = [
      {
        title: "Insumo",
        dataIndex: "descripcion",
        key: "descripcion",
        width: "40%",
        render: (value, record) => (
          <>
            <div>{value}</div>
            {esApartar && !record.apartable && (
              <Tooltip title="El apartado recrea las entradas de inventario, así que se perdería el lote/marbete elegido en el renglón">
                <Tag color="orange" className="square-tag" style={{ marginTop: 4 }}>
                  No apartable (lotes / marbetes)
                </Tag>
              </Tooltip>
            )}
          </>
        ),
      },
      {
        title: "Unidad",
        dataIndex: "unidad",
        key: "unidad",
        width: "10%",
        align: "center",
      },
      {
        title: "Cantidad",
        dataIndex: "cantidad",
        key: "cantidad",
        width: "12%",
        align: "right",
      },
      {
        title: "Apartado",
        dataIndex: "apartado",
        key: "apartado",
        width: "12%",
        align: "right",
      },
      ...(esApartar
        ? [
          {
            title: "Disponible",
            dataIndex: "disponible",
            key: "disponible",
            width: "12%",
            align: "right",
          },
        ]
        : []),
      {
        title: esApartar ? "A apartar" : "A desapartar",
        dataIndex: "valor",
        key: "valor",
        width: esApartar ? "14%" : "26%",
        align: "right",
        render: (value, record) => (
          <InputNumberItem
            value={value}
            min={0}
            max={record.maximo}
            disabled={!(record.maximo > 0)}
            onChange={(nuevo) => this.onChangeCantidad(record.index, nuevo)}
          />
        ),
      },
    ];

    return (
      <ModalItem
        title={
          esApartar
            ? "Apartar insumos - Orden " + (this.props.ordenNumero ?? "")
            : "Desapartar insumos - Orden " + (this.props.ordenNumero ?? "")
        }
        visible={this.props.visible}
        spinning={this.state.loading}
        confirmLoading={this.state.guardando}
        onCancel={() => this.props.cerrar(false)}
        onOk={this.aceptar.bind(this)}
        okText={esApartar ? "Apartar" : "Desapartar"}
        footer
        width={900}
      >
        <Table
          size="small"
          rowKey="key"
          dataSource={this.state.renglones}
          columns={columns}
          pagination={false}
          scroll={{ y: "50vh" }}
        />
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ModalApartarProductosAlCosto);
